import { useRef } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useLogger } from '~Hooks/Entities/useLogger';
import { useSelector } from '~Hooks';
import { UserMetadataInput } from '../../__queries__';
import { EMAIL_ORDER_RECEIPT, SET_ORDER_HAS_PRINTED } from '~Scenes/Handy/queries';
import {
  EmailOrderReceipt,
  EmailOrderReceiptVariables,
  SetOrderHasPrinted,
  SetOrderHasPrintedVariables,
} from '~Scenes/Handy/__queries__';
import {
  CreateSettingsBlueprint,
  CreateSettingsBlueprintVariables,
  UpdateOrderUser,
  UpdateOrderUserVariables,
} from './__queries__';

const CREATE_SETTINGS_BLUEPRINT = gql`
  mutation CreateSettingsBlueprint($data: CreateSettingsBlueprintInput!) {
    createSettingsBlueprint(data: $data) {
      error
      message
      success
    }
  }
`;

const UPDATE_ORDER_USER = gql`
  mutation UpdateOrderUser($id: ID!, $userId: String) {
    updateOrder(id: $id, userId: $userId) {
      id
      user {
        id
        name
        email
        phone
        orgNumber
        address
        zip
        city
        role
        venues {
          id
        }
      }
    }
  }
`;

type MutationResult<T> =
  | { error: null; success: true; data: T }
  | { error: string; success: false; data: null };

/**
 * Central place for all GraphQL API calls.
 *
 * Every call goes through `wrapMutation`, which logs failures via `useLogger`
 * so we get visibility into API errors in Datadog without repeating
 * try/catch boilerplate in every component.
 *
 * Never accept `companyId`, `venueId`, or other generic company/venue values
 * as params — pull them from `useMetadata` ([[useMetadata]]) or redux state
 * (`~reducers`/`useSelector`) inside the call instead of trusting the caller.
 */
const useApi = () => {
  const ignoreError = useRef(false);
  const logger = useLogger('api');
  const locale = useSelector((state) => state.locale);

  function wrapMutation<D, V extends Record<string, any>>(mutationFn: any, name: string) {
    return async (variables: V): Promise<MutationResult<D>> => {
      try {
        const { data } = await mutationFn({ variables });
        return { data: data as D, error: null, success: true };
      } catch (error) {
        const networkErrors = (error as any).networkError?.result?.errors || [];
        const graphQLErrors = (error as any).graphQLErrors || [];

        if (!ignoreError.current) {
          logger.error((error as Error).message, {
            err: error,
            variables,
            mutationName: name,
          });
        }

        return {
          success: false,
          error: [...networkErrors, ...graphQLErrors].flatMap((e: any) => e.message).join(', '),
          data: null,
        };
      } finally {
        ignoreError.current = false;
      }
    };
  }

  const [sendOrderEmail] = useMutation<EmailOrderReceipt, EmailOrderReceiptVariables>(
    EMAIL_ORDER_RECEIPT,
  );
  const [setOrderHasPrinted] = useMutation<SetOrderHasPrinted, SetOrderHasPrintedVariables>(
    SET_ORDER_HAS_PRINTED,
  );
  const [updateOrderUser] = useMutation<UpdateOrderUser, UpdateOrderUserVariables>(
    UPDATE_ORDER_USER,
  );
  const [createSettingsBlueprint] = useMutation<
    CreateSettingsBlueprint,
    CreateSettingsBlueprintVariables
  >(CREATE_SETTINGS_BLUEPRINT);

  const API = {
    muteError: () => {
      ignoreError.current = true;

      return API;
    },

    sendOrderEmail: async (input: {
      orderId: string;
      email?: string;
      userMetadata?: UserMetadataInput;
    }) => {
      // language is always taken from the logged-in locale, never passed by the caller
      const variables: EmailOrderReceiptVariables = { ...input, language: locale };

      logger.info('Sending order email', variables);

      return wrapMutation<EmailOrderReceipt, EmailOrderReceiptVariables>(
        sendOrderEmail,
        'sendOrderEmail',
      )(variables);
    },

    setOrderHasPrinted: async (input: SetOrderHasPrintedVariables) => {
      logger.info('Updating order hasPrintedReceiptCopy', input);

      return wrapMutation<SetOrderHasPrinted, SetOrderHasPrintedVariables>(
        setOrderHasPrinted,
        'setOrderHasPrinted',
      )(input);
    },

    updateOrderUser: async (input: UpdateOrderUserVariables) => {
      logger.info('Updating order user', input);

      return wrapMutation<UpdateOrderUser, UpdateOrderUserVariables>(
        updateOrderUser,
        'updateOrderUser',
      )(input);
    },

    createSettingsBlueprint: async (input: CreateSettingsBlueprintVariables) => {
      logger.info('Creating settings blueprint', input);

      return wrapMutation<CreateSettingsBlueprint, CreateSettingsBlueprintVariables>(
        createSettingsBlueprint,
        'createSettingsBlueprint',
      )(input);
    },
  };

  return API;
};

export default useApi;
